import styled from '@emotion/styled';
import { noop } from 'lodash';
import { Fragment, type FunctionComponent } from 'react';

import { Breadcrumbs } from '~/ui/core/breadcrumbs/Breadcrumbs';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { TableSkeleton } from '~/ui/core/table/Table';

import {
    COLUMNS_WIDTHS,
    Grid,
    GridBody,
    GridCell,
    GridHeader,
    GridHeaderCell,
    TableContainer,
} from '../tabs/overview/table/styles';

const SkeletonTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing['3xl'],
    marginBottom: theme.spacing.xl,
    width: '30%',
}));

const SkeletonContent = styled(SkeletonBase)(({ theme }) => ({
    height: '200px',
    marginBottom: theme.spacing.l,
    width: '100%',
}));

const SkeletonSkillRow = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.l,
    justifyContent: 'space-between',
    paddingTop: theme.spacing.s,
    width: '100%',
}));

const SkeletonSkillBadge = styled(SkeletonBase)(({ theme }) => ({
    flex: 1,
    height: theme.spacing['6xl'],
    maxWidth: '200px',
}));

const SkeletonProficiency = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing['3xl'],
    width: theme.spacing['8xl'],
}));

const SkeletonCategoryTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xxl,
    marginBottom: theme.spacing.xxs,
    marginTop: theme.spacing.xxs,
    width: '40%',
}));

const SkeletonCategoryContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    marginBottom: theme.spacing['4xl'],
    width: '100%',
}));

const SkeletonPageTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing['6xl'],
    marginBottom: theme.spacing['4xl'],
    width: '50%',
}));

const TitleContainer = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.m,
}));

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const SkeletonBreadcrumb = styled(SkeletonBase)(({}) => ({
    height: '1rem',
    width: '76px',
}));

export const SkeletonBreadcrumbs: FunctionComponent = () => {
    return (
        <Breadcrumbs
            items={[
                { label: <SkeletonBreadcrumb />, onClick: noop },
                { label: <SkeletonBreadcrumb />, onClick: noop },
                { label: <SkeletonBreadcrumb />, onClick: noop },
            ]}
        />
    );
};

export const DescriptionSectionSkeleton: FunctionComponent = () => {
    return (
        <div>
            <SkeletonTitle />
            <SkeletonContent />
        </div>
    );
};

export const CategoryListSkeleton: FunctionComponent = () => {
    return (
        <div>
            {[1, 2, 3].map((categoryIndex) => (
                <SkeletonCategoryContainer key={categoryIndex}>
                    <SkeletonCategoryTitle />
                    {[1, 2, 3, 4].map((skillIndex) => (
                        <SkeletonSkillRow key={`${categoryIndex}-${skillIndex}`}>
                            <SkeletonSkillBadge />
                            <SkeletonProficiency />
                        </SkeletonSkillRow>
                    ))}
                </SkeletonCategoryContainer>
            ))}
        </div>
    );
};

export const TitleSkeleton: FunctionComponent = () => {
    return (
        <TitleContainer>
            <SkeletonPageTitle />
        </TitleContainer>
    );
};

// Changelog Timeline Skeleton Components
const SkeletonTimelineItem = styled.div(({ theme }) => ({
    '&::before': {
        backgroundColor: theme.colors.gray[300],
        borderRadius: '50%',
        content: '""',
        height: '8px',
        left: '12px',
        position: 'absolute',
        top: '8px',
        width: '8px',
    },
    '&:not(:last-child)::after': {
        backgroundColor: theme.colors.gray[200],
        bottom: 0,
        content: '""',
        left: '15px',
        position: 'absolute',
        top: '20px',
        width: '2px',
    },
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.s,
    paddingBottom: theme.spacing['3xl'],
    paddingLeft: theme.spacing['4xl'],
    position: 'relative',
}));

const SkeletonTimelineTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    marginBottom: theme.spacing.xs,
    width: '70%',
}));

const SkeletonTimelineSubtitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.l,
    width: '40%',
}));

const SkeletonTimelineGroupLabel = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.l,
    marginBottom: theme.spacing.xl,
    width: '120px',
}));

export const ChangelogTimelineSkeleton: FunctionComponent = () => {
    return (
        <div>
            <SkeletonTimelineGroupLabel />
            {[1, 2, 3, 4].map((index) => (
                <SkeletonTimelineItem key={index}>
                    <SkeletonTimelineTitle />
                    <SkeletonTimelineSubtitle />
                </SkeletonTimelineItem>
            ))}
        </div>
    );
};

const SkeletonFrameworkCard = styled.div(({ theme }) => ({
    alignItems: 'center',
    backgroundColor: theme.colors.gray[100],
    borderRadius: theme.borderRadius.xl,
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing.m,
    justifyContent: 'space-between',
    padding: theme.spacing.xl,
}));

const SkeletonFrameworkContent = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing.xxs,
}));

const SkeletonFrameworkLabel = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.l,
    width: '120px',
}));

const SkeletonFrameworkTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing['3xl'],
    marginTop: theme.spacing.xxs,
    width: '60%',
}));

const SkeletonFrameworkDescription = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.l,
    marginTop: theme.spacing.xxs,
    width: '80%',
}));

const SkeletonFrameworkDescriptionSecondLine = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.l,
    marginTop: theme.spacing.xxs,
    width: '50%',
}));

const SkeletonFrameworkButton = styled(SkeletonBase)(({ theme }) => ({
    borderRadius: theme.borderRadius.m,
    height: theme.spacing['6xl'],
    width: '120px',
}));

const SkeletonFrameworksList = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.m,
}));

export const PredefinedFrameworkSkeleton: FunctionComponent = () => {
    return (
        <SkeletonFrameworksList>
            {[1, 2, 3, 4].map((index) => (
                <SkeletonFrameworkCard key={index}>
                    <SkeletonFrameworkContent>
                        <SkeletonFrameworkLabel />
                        <SkeletonFrameworkTitle />
                        <SkeletonFrameworkDescription />
                        <SkeletonFrameworkDescriptionSecondLine />
                    </SkeletonFrameworkContent>
                    <SkeletonFrameworkButton />
                </SkeletonFrameworkCard>
            ))}
        </SkeletonFrameworksList>
    );
};

const SkeletonSuggestionsWrapper = styled.div(({ theme }) => ({
    background: theme.colors.gray[50],
    border: `1px solid ${theme.colors.orange[800]}`,
    borderRadius: theme.borderRadius.l,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxl,
    padding: theme.spacing.xxl,
}));

const SkeletonSuggestionsHeadline = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing['3xl'],
    width: '200px',
}));

const SkeletonActionTitle = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '180px',
}));

const SkeletonSuggestionRow = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.xxl,
}));

const SkeletonCheckbox = styled(SkeletonBase)(({ theme }) => ({
    borderRadius: theme.borderRadius.s,
    height: theme.spacing.xxl,
    minWidth: theme.spacing.xxl,
    width: theme.spacing.xxl,
}));

const SkeletonSuggestionSkillBadge = styled(SkeletonBase)(({ theme }) => ({
    flex: 1,
    height: theme.spacing['6xl'],
    maxWidth: '200px',
}));

const SkeletonSuggestionSection = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxl,
    marginRight: '1.1rem',
    marginTop: theme.spacing.xxl,
}));

const SkeletonSuggestionsContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxl,
}));

const SkeletonActionButtonsContainer = styled.div(({ theme }) => ({
    display: 'grid',
    gap: theme.spacing.m,
    gridTemplateColumns: '1fr 2fr',
}));

const SkeletonActionButton = styled(SkeletonBase)(({ theme }) => ({
    borderRadius: theme.borderRadius.m,
    height: theme.spacing['6xl'],
}));

const SkeletonAiSuggestionsWrapper = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xxl,
    paddingBottom: theme.spacing.l,
}));

export const SkillsAiSuggestionsSkeleton: FunctionComponent = () => {
    return (
        <SkeletonAiSuggestionsWrapper>
            <SkeletonSuggestionsWrapper>
                <SkeletonSuggestionsHeadline />
                <SkeletonSuggestionSection>
                    <SkeletonActionTitle />
                    <SkeletonSuggestionsContainer>
                        {[1, 2, 3, 4, 5].map((index) => (
                            <SkeletonSuggestionRow key={index}>
                                <SkeletonCheckbox />
                                <SkeletonSuggestionSkillBadge />
                            </SkeletonSuggestionRow>
                        ))}
                    </SkeletonSuggestionsContainer>
                </SkeletonSuggestionSection>
            </SkeletonSuggestionsWrapper>
            <SkeletonActionButtonsContainer>
                <SkeletonActionButton />
                <SkeletonActionButton />
            </SkeletonActionButtonsContainer>
        </SkeletonAiSuggestionsWrapper>
    );
};

const SkeletonTableCell = styled(SkeletonBase)(({ theme }) => ({
    height: theme.spacing.xl,
    width: '80%',
}));

const SkeletonTableRow = styled.div(({ theme }) => ({
    backgroundColor: theme.colors.white[200],
    borderRadius: theme.borderRadius.l,
    display: 'grid',
    gap: theme.spacing.s,
    gridTemplateColumns: COLUMNS_WIDTHS.join(' '),
    marginTop: theme.spacing.s,
    padding: theme.spacing.s,
}));

// biome-ignore lint/correctness/noEmptyPattern: suppressed due to migration
const SkeletonTableBody = styled.div(({}) => ({
    display: 'grid',
    gridTemplateColumns: '1fr',
}));

export const JobArchitectureTableSkeleton: FunctionComponent = () => {
    return (
        <TableContainer>
            <Grid aria-label="Job Architecture Table" data-testid="loading-skeleton" role="grid">
                <GridHeader role="row">
                    {Array.from({ length: 8 }).map((_, index) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                        <GridHeaderCell key={index} role="columnheader">
                            <SkeletonTableCell />
                        </GridHeaderCell>
                    ))}
                </GridHeader>
                <GridBody>
                    <SkeletonTableBody>
                        {Array.from({ length: 5 }).map((_, rowIndex) => (
                            // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                            <SkeletonTableRow key={rowIndex}>
                                {Array.from({ length: 8 }).map((_, cellIndex) => (
                                    // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                                    <GridCell key={cellIndex}>
                                        <SkeletonTableCell />
                                    </GridCell>
                                ))}
                            </SkeletonTableRow>
                        ))}
                    </SkeletonTableBody>
                </GridBody>
            </Grid>
        </TableContainer>
    );
};

const SKILLS_COLUMNS_WIDTHS = [
    'minmax(300px, 1fr)',
    'minmax(200px, 1fr)',
    'minmax(200px, 1fr)',
    'minmax(80px, 1fr)',
    'minmax(80px, 1fr)',
    'minmax(80px, 1fr)',
    'minmax(80px, 1fr)',
    '48px',
];

export const SkillsTableSkeleton: FunctionComponent = () => {
    return (
        <TableSkeleton
            aria-label="Skills Table"
            columnWidths={SKILLS_COLUMNS_WIDTHS}
            data-testid="skills-table-skeleton"
            rowCount={8}
        />
    );
};

const SkillSearchResultSkeletonItem = styled.div(({ theme }) => ({
    padding: `${theme.spacing.xs} 0`,
}));

const SkillSearchResultSkeletonBadge = styled(SkeletonBase)(({ theme }) => ({
    borderRadius: theme.borderRadius.l,
    height: '30px',
    width: '200px',
}));

export const SkillSearchResultsSkeleton: FunctionComponent = () => {
    return (
        <Fragment>
            {Array.from({ length: 3 }).map((_, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: suppressed due to migration
                <SkillSearchResultSkeletonItem key={index}>
                    <SkillSearchResultSkeletonBadge />
                </SkillSearchResultSkeletonItem>
            ))}
        </Fragment>
    );
};
